/**
 * Parameter Converter Utility
 *
 * Converts between backend parameter format (dot notation) and UI format (camelCase)
 *
 * Backend format: "GridManagement.BuyGrid"
 * UI format: "buyGrid"
 *
 * @module parameterConverter
 */

/**
 * Mapping table: Backend Key → UI Key
 * Based on ADMIN_IMPLEMENTATION.md specification
 */
const BACKEND_TO_UI_MAP = {
    // Grid Management Parameters
    'GridManagement.GridThreshold': 'gridThreshold',
    'GridManagement.MaxGridLevels': 'maxGridLevels',
    'GridManagement.BuyGrid': 'buyGrid',
    'GridManagement.SellGrid': 'sellGrid',
    'GridManagement.BuyBaseLotSize': 'buyBaseLotSize',
    'GridManagement.SellBaseLotSize': 'sellBaseLotSize',
    'GridManagement.BuyLotSizeMultiplier': 'buyLotMultiplier',
    'GridManagement.SellLotSizeMultiplier': 'sellLotMultiplier',
    'GridManagement.MaxLotOpenedAllowed': 'maxLotAllowed',

    // Entry Settings
    'EntrySettings.TradeEntryMode': 'tradeEntryMode',

    // Take Profit Settings
    'TakeProfit.Enabled': 'tpGridEnabled',
    'TakeProfit.Mode': 'tpMode',
    'TakeProfit.Type': 'tpType',
    'TakeProfit.GridType': 'tpGridType',
    'TakeProfit.BuyLegTP': 'buyLegTP',
    'TakeProfit.SellLegTP': 'sellLegTP',
    'TakeProfit.BuyLegDL': 'buyLegDL',
    'TakeProfit.SellLegDL': 'sellLegDL',
    'TakeProfit.AccumulativeTP': 'accumulativeTP',

    // Stop Loss / Risk Management
    'StopLoss.Enabled': 'stopLossEnabled',
    'StopLoss.Pips': 'stopLossPips',
    'StopLoss.AccumulativeSL': 'accumulativeSL',

    // Legacy / Benchmark fields
    'Benchmark.ThresholdBuy': 'benchmarkThresholdBuy',
    'Benchmark.ThresholdSell': 'benchmarkThresholdSell'
};

/**
 * Hot-reloadable parameters - these can be changed while strategy is running
 * Based on backend API response validation
 * Parameters NOT in this list can only be changed when strategy is STOPPED
 */
const HOT_RELOADABLE_PARAMS = [
    // Confirmed working from API response
    'GridManagement.MaxGridLevels',
    'GridManagement.BuyGrid',
    'GridManagement.SellGrid',
    'GridManagement.BuyBaseLotSize',
    'GridManagement.SellBaseLotSize',
    'GridManagement.BuyLotSizeMultiplier',
    'GridManagement.SellLotSizeMultiplier',
    // Take Profit values (not enable/type/mode)
    'TakeProfit.BuyLegTP',
    'TakeProfit.SellLegTP',
    'TakeProfit.BuyLegDL',
    'TakeProfit.SellLegDL',
    'TakeProfit.AccumulativeTP',
    // Stop Loss value (not enable)
    'StopLoss.AccumulativeSL'
];

/**
 * Non-hot-reloadable parameters - require strategy to be STOPPED to change
 */
const REQUIRES_STOP_PARAMS = [
    'GridManagement.GridThreshold',
    'GridManagement.MaxLotOpenedAllowed',
    'EntrySettings.TradeEntryMode',
    'TakeProfit.Enabled',
    'TakeProfit.Mode',
    'TakeProfit.Type',
    'TakeProfit.GridType',
    'StopLoss.Enabled',
    'StopLoss.Pips',
    'Benchmark.ThresholdBuy',
    'Benchmark.ThresholdSell'
];

/**
 * Reverse mapping: UI Key → Backend Key
 * Generated from BACKEND_TO_UI_MAP
 */
const UI_TO_BACKEND_MAP = Object.entries(BACKEND_TO_UI_MAP).reduce((acc, [backendKey, uiKey]) => {
    acc[uiKey] = backendKey;
    return acc;
}, {});

/**
 * Convert backend parameters to UI format
 *
 * @param {Object} backendParams - Parameters object with dot notation keys
 * @returns {Object} Parameters object with camelCase UI keys
 *
 * @example
 * // Input
 * { "GridManagement.BuyGrid": 2, "GridManagement.SellGrid": 2 }
 *
 * // Output
 * { buyGrid: 2, sellGrid: 2 }
 */
export const backendToUI = (backendParams) => {
    if (!backendParams || typeof backendParams !== 'object') {
        console.warn('[parameterConverter] backendToUI received invalid input:', backendParams);
        return {};
    }

    const uiParams = {};

    Object.entries(backendParams).forEach(([backendKey, value]) => {
        const uiKey = BACKEND_TO_UI_MAP[backendKey];

        if (uiKey) {
            // Convert value to appropriate type
            uiParams[uiKey] = convertValue(value);
        } else {
            // If no mapping found, try to auto-convert the key
            // e.g., "SomeOther.Parameter" → "someOtherParameter"
            const autoKey = autoConvertKeyToUI(backendKey);
            uiParams[autoKey] = convertValue(value);
            console.debug(`[parameterConverter] Auto-converted unmapped key: ${backendKey} → ${autoKey}`);
        }
    });

    console.log('[parameterConverter] backendToUI conversion:', {
        input: backendParams,
        output: uiParams
    });

    return uiParams;
};

/**
 * Convert UI parameters to backend format
 *
 * @param {Object} uiParams - Parameters object with camelCase UI keys
 * @returns {Object} Parameters object with dot notation backend keys
 *
 * @example
 * // Input
 * { buyGrid: 2, sellGrid: 2 }
 *
 * // Output
 * { "GridManagement.BuyGrid": 2, "GridManagement.SellGrid": 2 }
 */
export const uiToBackend = (uiParams) => {
    if (!uiParams || typeof uiParams !== 'object') {
        console.warn('[parameterConverter] uiToBackend received invalid input:', uiParams);
        return {};
    }

    const backendParams = {};

    Object.entries(uiParams).forEach(([uiKey, value]) => {
        // Skip empty values (empty strings, null, undefined)
        if (value === '' || value === null || value === undefined) {
            return;
        }

        const backendKey = UI_TO_BACKEND_MAP[uiKey];

        if (backendKey) {
            backendParams[backendKey] = value;
        } else {
            // If no mapping found, skip (UI-only fields)
            console.debug(`[parameterConverter] Skipping UI-only field: ${uiKey}`);
        }
    });

    console.log('[parameterConverter] uiToBackend conversion:', {
        input: uiParams,
        output: backendParams
    });

    return backendParams;
};

/**
 * Convert a value to its appropriate type
 * Handles string numbers, booleans, etc.
 *
 * @param {any} value - The value to convert
 * @returns {any} The converted value
 */
const convertValue = (value) => {
    // Handle null/undefined
    if (value === null || value === undefined) {
        return '';
    }

    // Handle booleans
    if (typeof value === 'boolean') {
        return value;
    }

    // Handle string booleans
    if (value === 'true') return true;
    if (value === 'false') return false;

    // Handle numbers (keep as-is, convert to string for input fields)
    if (typeof value === 'number') {
        return value.toString();
    }

    // Return strings as-is
    return value;
};

/**
 * Auto-convert a backend key to UI format
 * "SomeCategory.ParameterName" → "parameterName"
 *
 * @param {string} backendKey - The backend key with dot notation
 * @returns {string} The camelCase UI key
 */
const autoConvertKeyToUI = (backendKey) => {
    // Split by dot and take the last part
    const parts = backendKey.split('.');
    const lastPart = parts[parts.length - 1];

    // Convert first character to lowercase (PascalCase → camelCase)
    return lastPart.charAt(0).toLowerCase() + lastPart.slice(1);
};

/**
 * Merge fetched parameters with default UI state
 * Ensures all UI fields have values (uses defaults for missing params)
 *
 * @param {Object} fetchedParams - Parameters fetched from API (already converted to UI format)
 * @param {Object} defaultParams - Default UI parameter state
 * @returns {Object} Merged parameters
 */
export const mergeWithDefaults = (fetchedParams, defaultParams) => {
    const merged = { ...defaultParams };

    Object.entries(fetchedParams).forEach(([key, value]) => {
        if (key in merged) {
            merged[key] = value;
        }
    });

    return merged;
};

/**
 * Get the list of all mapped backend keys
 * Useful for filtering API responses
 *
 * @returns {string[]} Array of backend keys
 */
export const getBackendKeys = () => Object.keys(BACKEND_TO_UI_MAP);

/**
 * Get the list of all mapped UI keys
 * Useful for validation
 *
 * @returns {string[]} Array of UI keys
 */
export const getUIKeys = () => Object.values(BACKEND_TO_UI_MAP);

/**
 * Check if a backend parameter key is hot-reloadable
 *
 * @param {string} backendKey - The backend parameter key
 * @returns {boolean} True if the parameter can be changed while running
 */
export const isHotReloadable = (backendKey) => HOT_RELOADABLE_PARAMS.includes(backendKey);

/**
 * Get UI keys that require strategy to be stopped before changing
 *
 * @returns {string[]} Array of UI keys that require stop
 */
export const getRequiresStopUIKeys = () => {
    return REQUIRES_STOP_PARAMS.map(backendKey => BACKEND_TO_UI_MAP[backendKey]).filter(Boolean);
};

/**
 * Convert UI parameters to backend format, filtering for hot-reloadable only
 * Use this when the strategy is RUNNING
 *
 * @param {Object} uiParams - Parameters object with camelCase UI keys
 * @returns {Object} Parameters object with only hot-reloadable backend keys
 */
export const uiToBackendHotReloadable = (uiParams) => {
    if (!uiParams || typeof uiParams !== 'object') {
        console.warn('[parameterConverter] uiToBackendHotReloadable received invalid input:', uiParams);
        return {};
    }

    const backendParams = {};
    const skippedParams = [];

    Object.entries(uiParams).forEach(([uiKey, value]) => {
        // Skip empty values
        if (value === '' || value === null || value === undefined) {
            return;
        }

        const backendKey = UI_TO_BACKEND_MAP[uiKey];

        if (backendKey) {
            if (HOT_RELOADABLE_PARAMS.includes(backendKey)) {
                backendParams[backendKey] = value;
            } else {
                skippedParams.push({ uiKey, backendKey, reason: 'requires_stop' });
            }
        }
    });

    console.log('[parameterConverter] uiToBackendHotReloadable conversion:', {
        input: uiParams,
        output: backendParams,
        skipped: skippedParams
    });

    return { params: backendParams, skipped: skippedParams };
};

/**
 * Identify which parameters have changed and categorize them
 *
 * @param {Object} originalParams - Original UI parameters
 * @param {Object} newParams - New UI parameters
 * @returns {Object} { hotReloadable: {...}, requiresStop: {...} }
 */
export const categorizeChangedParams = (originalParams, newParams) => {
    const hotReloadable = {};
    const requiresStop = {};

    Object.entries(newParams).forEach(([uiKey, newValue]) => {
        const originalValue = originalParams[uiKey];

        // Check if value changed (convert to string for comparison)
        if (String(newValue) !== String(originalValue) && newValue !== '' && newValue !== null) {
            const backendKey = UI_TO_BACKEND_MAP[uiKey];

            if (backendKey) {
                if (HOT_RELOADABLE_PARAMS.includes(backendKey)) {
                    hotReloadable[backendKey] = newValue;
                } else if (REQUIRES_STOP_PARAMS.includes(backendKey)) {
                    requiresStop[backendKey] = newValue;
                }
            }
        }
    });

    return { hotReloadable, requiresStop };
};

export default {
    backendToUI,
    uiToBackend,
    uiToBackendHotReloadable,
    mergeWithDefaults,
    getBackendKeys,
    getUIKeys,
    isHotReloadable,
    getRequiresStopUIKeys,
    categorizeChangedParams
};
